<!-- eslint-disable vue/no-v-html -->
<template>
  <section class="py-12">
    <div class="container-wrap">
      <h2 class="text-h2 mb-6">{{ t('title') }}</h2>

      <ul
        class="scrollbar-h-px extend-width mb-6 flex w-full overflow-y-hidden overflow-x-scroll desktop:flex-wrap desktop:overflow-x-hidden"
      >
        <li v-for="(tab, index) in tabs" :key="tab" class="mr-2 desktop:mb-2">
          <button
            class="w-full rounded-3xl border border-solid border-gray-400 px-4 py-1.5 hover:bg-blue-400 hover:text-white"
            :class="
              current === index
                ? 'bg-blue-400 text-white'
                : 'bg-white text-blue-400'
            "
            :title="tab"
            @click="current = index"
          >
            <span class="truncate text-left text-sm font-bold tablet:text-base">
              {{ tab }}
            </span>
          </button>
        </li>
      </ul>

      <div
        v-show="current === 0"
        class="rounded border border-solid border-gray-400"
      >
        <dl
          v-for="item in minHardwareItems"
          :key="item.label"
          class="border-b border-solid border-gray-400 p-3 last:border-b-0 tablet:flex tablet:p-0"
        >
          <dt class="tablet:basis-60 tablet:px-5 tablet:py-3">
            <p class="text-sm font-bold tablet:text-base">{{ item.label }}</p>
          </dt>
          <dd class="tablet:flex-1 tablet:px-5 tablet:py-3">
            <p class="text-sm tablet:text-base">{{ item.content }}</p>
          </dd>
        </dl>
      </div>

      <div
        v-show="current === 1"
        class="rounded border border-solid border-gray-400"
      >
        <div class="flex flex-col tablet:flex-row">
          <div
            v-for="protocol in protocols"
            :key="protocol.label"
            class="flex-1 first:border-b first:border-solid first:border-gray-400 tablet:first:border-b-0"
          >
            <div
              class="px-4 py-2 tablet:border-b tablet:border-solid tablet:border-gray-400 tablet:bg-gray-100 tablet:px-5 tablet:py-3"
            >
              <p class="text-sm font-bold tablet:text-base">
                {{ protocol.label }}
              </p>
            </div>
            <div
              class="markdown-content px-4 pb-2 tablet:px-5 tablet:py-3"
              v-html="provideMarkdownHtml(protocol.content, 'simple')"
            />
          </div>
        </div>
      </div>

      <div
        v-show="current === 2"
        class="rounded border border-solid border-gray-400 p-5"
      >
        <ul class="list-disc pl-5">
          <li
            v-for="lang in sortAvailableLanguages"
            :key="lang"
            class="text-sm tablet:text-base"
          >
            {{ getLanguageName(lang) }}
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { EnumLanguage } from '@/assets/types/api/Products';

  const { t } = useI18n({ useScope: 'local' });

  const pricingStore = usePricingStore();
  const { pricingAdditionalInfo } = storeToRefs(pricingStore);

  const current = ref<number>(0);

  const tabs = computed((): string[] => [
    t('tab-min-hardware'),
    t('tab-protocols'),
    t('tab-available-languages'),
  ]);

  const minHardwareItems = computed(() => [
    {
      label: t('operating-system'),
      content: pricingAdditionalInfo.value.minHardware.operatingSystem,
    },
    {
      label: t('container-deployment'),
      content: pricingAdditionalInfo.value.minHardware.containerDeployment,
    },
    {
      label: t('hardware'),
      content: pricingAdditionalInfo.value.minHardware.hardware,
    },
    {
      label: t('display-resolution'),
      content: pricingAdditionalInfo.value.minHardware.displayResolution,
    },
    { label: t('usb'), content: pricingAdditionalInfo.value.minHardware.usb },
    {
      label: t('network-environment'),
      content: pricingAdditionalInfo.value.minHardware.networkEnvironment,
    },
  ]);

  const protocols = computed(() => [
    {
      label: t('data-acquisition-protocols'),
      content: pricingAdditionalInfo.value.protocols.dataAcquisition,
    },
    {
      label: t('data-forwarding-protocols'),
      content: pricingAdditionalInfo.value.protocols.dataForwarding,
    },
  ]);

  const AVAILABLE_LANGUAGE_ORDER: string[] = [
    EnumLanguage.EN_US,
    EnumLanguage.ZH_TW,
    EnumLanguage.JA_JP,
    EnumLanguage.KO_KR,
    EnumLanguage.TH_TH,
    EnumLanguage.VI_VN,
    EnumLanguage.ZH_CN,
  ];

  const sortAvailableLanguages = computed(() =>
    [...pricingAdditionalInfo.value.availableLanguages].sort(
      (a, b) =>
        AVAILABLE_LANGUAGE_ORDER.indexOf(a) -
        AVAILABLE_LANGUAGE_ORDER.indexOf(b)
    )
  );

  const getLanguageName = (code: EnumLanguage): string => {
    switch (code) {
      case EnumLanguage.EN_US:
        return 'English';
      case EnumLanguage.ZH_TW:
        return '繁體中文';
      case EnumLanguage.JA_JP:
        return '日本語';
      case EnumLanguage.KO_KR:
        return '한국어';
      case EnumLanguage.TH_TH:
        return 'ภาษาไทย';
      case EnumLanguage.VI_VN:
        return 'Tiếng Việt';
      case EnumLanguage.ZH_CN:
        return '简体中文';
      default:
        return code;
    }
  };
</script>

<style scoped lang="scss">
  .markdown-content {
    :deep(ul),
    :deep(ol),
    :deep(p),
    :deep(a) {
      @apply text-black;
    }
  }
</style>

<i18n lang="json">
{
  "en-int": {
    "title": "Additional Information",
    "tab-min-hardware": "Minimum Hardware Requirements",
    "tab-protocols": "Protocols",
    "tab-available-languages": "Available Languages",
    "operating-system": "Operating System",
    "container-deployment": "Container Deployment",
    "hardware": "Hardware",
    "display-resolution": "Display Resolution",
    "usb": "USB",
    "network-environment": "Network Environment",
    "data-acquisition-protocols": "Data Acquisition Protocols List",
    "data-forwarding-protocols": "Data Forwarding Protocols List"
  },
  "en-jp": {
    "title": "Additional Information",
    "tab-min-hardware": "Minimum Hardware Requirements",
    "tab-protocols": "Protocols",
    "tab-available-languages": "Available Languages",
    "operating-system": "Operating System",
    "container-deployment": "Container Deployment",
    "hardware": "Hardware",
    "display-resolution": "Display Resolution",
    "usb": "USB",
    "network-environment": "Network Environment",
    "data-acquisition-protocols": "Data Acquisition Protocols List",
    "data-forwarding-protocols": "Data Forwarding Protocols List"
  },
  "en-tw": {
    "title": "Additional Information",
    "tab-min-hardware": "Minimum Hardware Requirements",
    "tab-protocols": "Protocols",
    "tab-available-languages": "Available Languages",
    "operating-system": "Operating System",
    "container-deployment": "Container Deployment",
    "hardware": "Hardware",
    "display-resolution": "Display Resolution",
    "usb": "USB",
    "network-environment": "Network Environment",
    "data-acquisition-protocols": "Data Acquisition Protocols List",
    "data-forwarding-protocols": "Data Forwarding Protocols List"
  },
  "en-kr": {
    "title": "Additional Information",
    "tab-min-hardware": "Minimum Hardware Requirements",
    "tab-protocols": "Protocols",
    "tab-available-languages": "Available Languages",
    "operating-system": "Operating System",
    "container-deployment": "Container Deployment",
    "hardware": "Hardware",
    "display-resolution": "Display Resolution",
    "usb": "USB",
    "network-environment": "Network Environment",
    "data-acquisition-protocols": "Data Acquisition Protocols List",
    "data-forwarding-protocols": "Data Forwarding Protocols List"
  },
  "en-americas": {
    "title": "Additional Information",
    "tab-min-hardware": "Minimum Hardware Requirements",
    "tab-protocols": "Protocols",
    "tab-available-languages": "Available Languages",
    "operating-system": "Operating System",
    "container-deployment": "Container Deployment",
    "hardware": "Hardware",
    "display-resolution": "Display Resolution",
    "usb": "USB",
    "network-environment": "Network Environment",
    "data-acquisition-protocols": "Data Acquisition Protocols List",
    "data-forwarding-protocols": "Data Forwarding Protocols List"
  },
  "en-asia-pacific": {
    "title": "Additional Information",
    "tab-min-hardware": "Minimum Hardware Requirements",
    "tab-protocols": "Protocols",
    "tab-available-languages": "Available Languages",
    "operating-system": "Operating System",
    "container-deployment": "Container Deployment",
    "hardware": "Hardware",
    "display-resolution": "Display Resolution",
    "usb": "USB",
    "network-environment": "Network Environment",
    "data-acquisition-protocols": "Data Acquisition Protocols List",
    "data-forwarding-protocols": "Data Forwarding Protocols List"
  },
  "en-emea": {
    "title": "Additional Information",
    "tab-min-hardware": "Minimum Hardware Requirements",
    "tab-protocols": "Protocols",
    "tab-available-languages": "Available Languages",
    "operating-system": "Operating System",
    "container-deployment": "Container Deployment",
    "hardware": "Hardware",
    "display-resolution": "Display Resolution",
    "usb": "USB",
    "network-environment": "Network Environment",
    "data-acquisition-protocols": "Data Acquisition Protocols List",
    "data-forwarding-protocols": "Data Forwarding Protocols List"
  },
  "ja-jp": {
    "title": "詳細はこちら",
    "tab-min-hardware": "最小ハードウェア要件",
    "tab-protocols": "対応プロトコル",
    "tab-available-languages": "対応言語",
    "operating-system": "オペレーティングシステム",
    "container-deployment": "コンテナデプロイメント",
    "hardware": "ハードウェア",
    "display-resolution": "ディスプレイ解像度",
    "usb": "USB",
    "network-environment": "ネットワーク環境",
    "data-acquisition-protocols": "データ取得プロトコル一覧",
    "data-forwarding-protocols": "データ転送プロトコル一覧"
  },
  "zh-cn": {
    "title": "更多资讯",
    "tab-min-hardware": "最低硬件要求",
    "tab-protocols": "通信协议",
    "tab-available-languages": "支持语言",
    "operating-system": "操作系统",
    "container-deployment": "容器部署",
    "hardware": "硬件",
    "display-resolution": "显示分辨率",
    "usb": "USB",
    "network-environment": "网络环境",
    "data-acquisition-protocols": "数据采集协议列表",
    "data-forwarding-protocols": "数据转发协议列表"
  },
  "zh-tw": {
    "title": "更多資訊",
    "tab-min-hardware": "最低硬體需求",
    "tab-protocols": "通訊協定",
    "tab-available-languages": "支援語言",
    "operating-system": "操作系統",
    "container-deployment": "容器部署",
    "hardware": "硬體",
    "display-resolution": "螢幕解析度",
    "usb": "USB",
    "network-environment": "網路環境",
    "data-acquisition-protocols": "資料擷取協議清單",
    "data-forwarding-protocols": "資料轉發協議清單"
  }
}
</i18n>
